import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, HeartPulse, MessageCircle, ShieldCheck, Stethoscope, Truck, Leaf } from "lucide-react";
import { api, FEATURED_ORDER, HERO, HORSE_2, HORSE_3, MASCOT } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { PostCard } from "@/pages/Blog";
import { ListingCard } from "@/pages/Market";

const Hero = () => (
  <section className="relative bg-[#0B1D3A] text-white overflow-hidden" data-testid="home-hero">
    <img src={HERO} alt="Caballo al galope" className="absolute inset-0 w-full h-full object-cover opacity-60" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0B1D3A] via-[#0B1D3A]/80 to-transparent" />
    <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-28 lg:py-36">
      <span className="rise inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase bg-[#DC2626] px-3 py-1.5 rounded-sm">
        Piensos complementarios para caballos
      </span>
      <h1 className="rise d1 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.92] mt-6 max-w-3xl">
        Nutrición natural para caballos que dan <span className="text-[#60A5FA]">lo mejor de sí</span>
      </h1>
      <p className="rise d2 mt-6 text-base sm:text-lg text-blue-100 max-w-xl">
        Complementos nutricionales y productos de salud e higiene para el caballo que trabaja, compite y se esfuerza cada día.
      </p>
      <div className="rise d3 mt-9 flex flex-wrap gap-3">
        <Link to="/tienda" data-testid="hero-shop-button" className="bg-[#DC2626] hover:bg-[#B91C1C] font-display font-bold text-lg px-7 py-3.5 rounded-full transition-colors flex items-center gap-2">
          Ver productos <ArrowRight size={18} />
        </Link>
        <Link to="/calculadora" data-testid="hero-calculator-button" className="bg-white text-[#0B1D3A] hover:bg-[#EFF6FF] font-display font-bold text-lg px-7 py-3.5 rounded-full transition-colors">
          Calcula su dieta
        </Link>
      </div>
    </div>
  </section>
);

const Marquee = () => {
  const words = ["Digestión", "Articulaciones", "Piel", "Hidratación", "Músculo", "Cascos y crines", "Calma"];
  const row = [...words, ...words, ...words, ...words];
  return (
    <div className="bg-[#DC2626] text-white overflow-hidden py-3">
      <div className="marquee flex gap-10 whitespace-nowrap w-max">
        {row.map((w, i) => <span key={i} className="font-display font-extrabold text-xl tracking-wide">{w} <span className="text-white/50 ml-8">✦</span></span>)}
      </div>
    </div>
  );
};

const Featured = ({ products }) => (
  <section className="max-w-7xl mx-auto px-4 pt-20" data-testid="featured-section">
    <div className="flex items-end justify-between gap-6 mb-10">
      <div>
        <p className="text-[#DC2626] font-bold text-sm tracking-[0.2em] uppercase">Destacados FAUNNY</p>
        <h2 className="font-display font-black text-4xl sm:text-5xl text-[#0B1D3A] mt-2">Lo que más necesitan</h2>
      </div>
      <Link to="/tienda" className="hidden sm:flex font-display font-bold text-[#0B1D3A] hover:text-[#DC2626] items-center gap-1">Ver todo <ArrowRight size={16} /></Link>
    </div>
    <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      {products.map((p) => <ProductCard key={p.slug} p={p} highlight={p.featured_text} />)}
    </div>
  </section>
);

const LineCard = ({ to, title, text, image, testid, pos = "center" }) => (
  <Link to={to} data-testid={testid} className="group rounded overflow-hidden flex flex-col bg-[#0B1D3A]">
    <div className="h-64 sm:h-72 overflow-hidden">
      <img src={image} alt={title} style={{ objectPosition: pos }} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
    </div>
    <div className="stripe-bar h-1" />
    <div className="p-7 text-white flex-1">
      <h3 className="font-display font-black text-3xl sm:text-4xl leading-none">{title}</h3>
      <p className="text-blue-100 mt-3 max-w-md text-sm">{text}</p>
      <span className="inline-flex items-center gap-2 mt-5 font-display font-bold bg-[#DC2626] px-5 py-2.5 rounded-full">Ver productos <ArrowRight size={16} /></span>
    </div>
  </Link>
);

const Tools = () => {
  const items = [
    { to: "/calculadora", icon: Calculator, t: "Calculadora de dieta", d: "Ración diaria de forraje, pienso, agua y sal según peso, edad, raza y actividad.", id: "home-tool-calculator" },
    { to: "/diagnostico", icon: Stethoscope, t: "Diagnóstico FAUNNY", d: "Cuéntanos qué le pasa y te damos nuestra opinión y cuándo llamar al veterinario.", id: "home-tool-diagnosis" },
    { to: "/guia", icon: HeartPulse, t: "Guía de salud", d: "Elige qué le pasa a tu caballo y descubre qué productos FAUNNY le ayudan.", id: "home-tool-guide" },
    { to: "/asesor", icon: MessageCircle, t: "Asesor FAUNNY", d: "Describe a tu caballo y recibe recomendaciones al momento con IA.", id: "home-tool-advisor" },
  ];
  return (
    <section className="bg-[#EFF6FF] mt-24 py-20">
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-[1fr_1.4fr] gap-12 items-center">
        <div className="relative">
          <img src={MASCOT} alt="Dr. FAUNNY, el caballo doctor" className="w-full max-w-md aspect-square object-cover object-center rounded shadow-[0_30px_60px_-30px_rgba(11,29,58,0.6)]" data-testid="home-mascot" />
          <span className="absolute -bottom-4 left-6 bg-[#DC2626] text-white font-display font-bold px-4 py-2 rounded-sm">Dr. FAUNNY te asesora</span>
        </div>
        <div>
          <h2 className="font-display font-black text-4xl sm:text-5xl text-[#0B1D3A]">¿No sabes qué necesita tu caballo?</h2>
          <p className="text-slate-600 mt-4 max-w-xl">Te ayudamos a elegir el producto más conveniente según sus necesidades, de forma gratuita y sin necesidad de cuenta.</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            {items.map(({ to, icon: Icon, t, d, id }) => (
              <Link key={to} to={to} data-testid={id} className="bg-white p-5 rounded border border-transparent hover:border-[#2563EB] transition-colors group">
                <Icon className="text-[#DC2626]" size={28} />
                <h3 className="font-display font-extrabold text-xl text-[#0B1D3A] mt-3 leading-tight">{t}</h3>
                <p className="text-sm text-slate-600 mt-2">{d}</p>
                <span className="inline-flex mt-3 text-sm font-bold text-[#2563EB] group-hover:text-[#DC2626]">Empezar →</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Trust = () => (
  <section className="max-w-7xl mx-auto px-4 pt-20 grid sm:grid-cols-3 gap-8">
    {[[Leaf, "Enfoque natural", "Ingredientes de origen natural y fórmulas libres de doping."], [Truck, "Envío gratis desde 100 €", "Por debajo, solo 4,95 € a toda la península."], [ShieldCheck, "Pago 100 % seguro", "Tarjeta o Bizum en la pasarela segura de Redsys."]].map(([Icon, t, d]) => (
      <div key={t} className="flex gap-4">
        <div className="w-12 h-12 shrink-0 rounded-full bg-[#0B1D3A] text-white grid place-items-center"><Icon size={22} /></div>
        <div><h3 className="font-display font-extrabold text-xl text-[#0B1D3A]">{t}</h3><p className="text-sm text-slate-600 mt-1">{d}</p></div>
      </div>
    ))}
  </section>
);

const FeaturedListings = () => {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/listings-featured").then((r) => setList(r.data.slice(0, 4))).catch(() => {}); }, []);
  if (!list.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 pt-20" data-testid="home-featured-listings">
      <div className="flex items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-[#D97706] font-bold text-sm tracking-[0.2em] uppercase">Mercado FAUNNY · Destacados</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl text-[#0B1D3A] mt-2">Anuncios destacados</h2>
        </div>
        <Link to="/mercado" data-testid="home-market-all" className="font-display font-bold text-[#0B1D3A] hover:text-[#DC2626] flex items-center gap-1 whitespace-nowrap">Ver el mercado <ArrowRight size={16} /></Link>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{list.map((x) => <ListingCard key={x.id} x={x} />)}</div>
    </section>
  );
};

const LatestPosts = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data.slice(0, 3))).catch(() => {}); }, []);
  if (!posts.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 pt-20" data-testid="home-blog-section">
      <div className="flex items-end justify-between gap-6 mb-10">
        <div>
          <p className="text-[#DC2626] font-bold text-sm tracking-[0.2em] uppercase">Blog FAUNNY</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl text-[#0B1D3A] mt-2">Consejos para tu caballo</h2>
        </div>
        <Link to="/blog" data-testid="home-blog-all" className="hidden sm:flex font-display font-bold text-[#0B1D3A] hover:text-[#DC2626] items-center gap-1">Ver todos <ArrowRight size={16} /></Link>
      </div>
      <div className="grid md:grid-cols-3 gap-5">{posts.map((p) => <PostCard key={p.slug} p={p} />)}</div>
      <Link to="/blog" className="sm:hidden mt-6 inline-flex font-display font-bold text-[#DC2626] items-center gap-1">Ver todos los artículos <ArrowRight size={16} /></Link>
    </section>
  );
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) =>
      setFeatured([...r.data].sort((a, b) => (FEATURED_ORDER.indexOf(a.slug) + 99) % 99 - (FEATURED_ORDER.indexOf(b.slug) + 99) % 99)));
  }, []);
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <Featured products={featured} />
      <section className="max-w-7xl mx-auto px-4 pt-20 grid md:grid-cols-2 gap-5">
        <LineCard to="/tienda?line=complementos" testid="line-card-complementos" image={HORSE_2} pos="center 28%" title="Complementos nutricionales"
          text="Fórmulas que impulsan el rendimiento de tu caballo, refuerzan su salud y cubren todas sus necesidades energéticas." />
        <LineCard to="/tienda?line=salud" testid="line-card-salud" image={HORSE_3} pos="center 30%" title="Salud e higiene"
          text="Cuidado integral para proteger, limpiar y mantener a tu caballo en perfecto estado, de los cascos a la crin." />
      </section>
      <Tools />
      <FeaturedListings />
      <LatestPosts />
      <Trust />
    </div>
  );
}
